// src/components/KhauGaliMetaverseMap.jsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMinus,
  faPlus,
  faTimes,
  faCopy,
  faStar,
  faStarHalfAlt,
  faCheckCircle,
  faTag,
} from '@fortawesome/free-solid-svg-icons';

/**
 * Props:
 *  - rows (number) default 6
 *  - cols (number) default 8
 *  - dataset (array) default []  // stall objects: { id, name (stall name), vendor (owner), dish, priceRange, rating, reviews, img, offer, verified, coords: {r,c}, location (e.g., "Chaat Corner") }
 *  - ownerId (string) optional - highlights player's owned stalls
 *  - onContact(stall) optional callback
 *  - onScheduleVisit(stall) optional callback
 */
export default function KhauGaliMetaverseMap({
  rows = 6,
  cols = 8,
  dataset = [],
  ownerId = null,
  onContact = (stall) => alert(`Contact requested for ${stall.name} (dummy)`),
  onScheduleVisit = (stall) => {
    const dt = window.prompt('Preferred visit date/time (dummy):', '');
    if (!dt) { alert('Visit cancelled'); return; }
    alert(`Visit scheduled for ${stall.name} on ${dt} (dummy)`);
  },
}) {
  const [zoom, setZoom] = useState(1);
  const [showVendors, setShowVendors] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCell, setSelectedCell] = useState(null);
  const [mapGrid, setMapGrid] = useState([]);
  const [ratingScaleDomain, setRatingScaleDomain] = useState([3, 5]);
  const containerRef = useRef(null);
  const touchState = useRef({ initialDistance: null, initialZoom: null });
  const listings = dataset || [];

  // Compute min/max rating for heatmap
  useEffect(() => {
    const ratings = listings
      .map(l => Number(l.rating || 0))
      .filter(r => !isNaN(r) && r > 0);
    if (ratings.length) {
      const min = Math.min(...ratings);
      const max = Math.max(...ratings);
      setRatingScaleDomain([min > 3 ? min : 3, max < 5 ? max : 5]);
    }
  }, [dataset]);

  // Build grid
  useEffect(() => {
    const total = rows * cols;
    const grid = Array.from({ length: rows }).map(() =>
      Array.from({ length: cols }).map(() => ({ items: [] }))
    );
    const clamp = (v, max) => Math.max(0, Math.min(max - 1, v));
    let idx = 0;
    for (const stall of listings) {
      if (stall?.coords?.r !== undefined && stall?.coords?.c !== undefined) {
        const r = clamp(stall.coords.r, rows);
        const c = clamp(stall.coords.c, cols);
        grid[r][c].items.push(stall);
      } else {
        const pos = idx % total;
        const r = Math.floor(pos / cols);
        const c = pos % cols;
        grid[r][c].items.push(stall);
        idx++;
      }
    }
    setMapGrid(grid);
  }, [dataset, rows, cols, listings]);

  // Rating to color gradient (low: dark gray → mid: blue → high: bright cyan)
  const ratingToColor = (rating) => {
    const [min, max] = ratingScaleDomain;
    if (!rating || isNaN(rating)) return '#111827'; // gray-900
    const t = max === min ? 0.5 : Math.max(0, Math.min(1, (rating - min) / (max - min)));
    const lerp = (a, b, v) => Math.round(a + (b - a) * v);
    const low = [17, 24, 39];     // gray-900
    const mid = [30, 64, 175];    // blue-800
    const high = [6, 182, 212];   // cyan-500
    const rgb = t < 0.5
      ? [lerp(low[0], mid[0], t / 0.5), lerp(low[1], mid[1], t / 0.5), lerp(low[2], mid[2], t / 0.5)]
      : [lerp(mid[0], high[0], (t - 0.5) / 0.5), lerp(mid[1], high[1], (t - 0.5) / 0.5), lerp(mid[2], high[2], (t - 0.5) / 0.5)];
    return `rgb(${rgb.join(',')})`;
  };

  const renderStars = (rating) => {
    const integerPart = Math.floor(rating);
    const hasHalf = rating - integerPart >= 0.5;
    return (
      <div className="flex items-center">
        {[...Array(5)].map((_, i) => (
          <FontAwesomeIcon
            key={i}
            icon={i < integerPart ? faStar : i === integerPart && hasHalf ? faStarHalfAlt : faStar}
            className={i < integerPart || (i === integerPart && hasHalf) ? "text-cyan-400" : "text-gray-600"}
            size="xs"
          />
        ))}
      </div>
    );
  };

  const totalItems = useMemo(() => listings.length, [listings]);
  const cellMatchesSearch = (cell) => {
    if (!search) return true;
    const q = search.trim().toLowerCase();
    return cell.items.some(stall =>
      stall.name?.toLowerCase().includes(q) ||
      stall.dish?.toLowerCase().includes(q) ||
      stall.location?.toLowerCase().includes(q)
    );
  };

  const openCell = (r, c) => {
    const items = mapGrid[r]?.[c]?.items || [];
    setSelectedCell({ r, c, items });
  };
  const closeDetails = () => setSelectedCell(null);

  // Responsive width
  const [containerWidth, setContainerWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const updateWidth = () => {
      const w = containerRef.current?.clientWidth || window.innerWidth;
      setContainerWidth(w);
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    let ro;
    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateWidth);
      ro.observe(containerRef.current);
    }
    return () => {
      window.removeEventListener('resize', updateWidth);
      if (ro && containerRef.current) ro.unobserve(containerRef.current);
    };
  }, []);

  const BASE_CELL = 120;
  const cellMin = useMemo(() => {
    const maxAllowed = Math.floor((containerWidth - 16) / cols);
    const desired = Math.round(BASE_CELL * zoom);
    return Math.max(72, Math.min(desired, Math.max(72, maxAllowed)));
  }, [containerWidth, cols, zoom]);

  const clampZoomForContainer = (z) => {
    const maxAllowed = Math.max(72, Math.floor((containerWidth - 16) / cols));
    const maxZoom = Math.max(0.6, maxAllowed / BASE_CELL);
    return Math.max(0.5, Math.min(z, maxZoom * 2.5));
  };

  // Touch pinch-to-zoom (mobile)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const getDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const onTouchStart = (e) => {
      if (e.touches?.length === 2) {
        touchState.current.initialDistance = getDistance(e.touches);
        touchState.current.initialZoom = zoom;
      }
    };

    const onTouchMove = (e) => {
      if (e.touches?.length === 2 && touchState.current.initialDistance) {
        if (e.cancelable) e.preventDefault();
        const newDist = getDistance(e.touches);
        setZoom(clampZoomForContainer(touchState.current.initialZoom * (newDist / touchState.current.initialDistance)));
      }
    };

    const onTouchEnd = () => {
      touchState.current.initialDistance = null;
      touchState.current.initialZoom = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [zoom, containerWidth, cols]);

  if (!mapGrid?.length) return <div className="p-4 text-blue-300">Loading Khau Gali map...</div>;

  return (
    <div className="mt-4 w-full overflow-x-hidden" ref={containerRef}>
      {/* Controls - Dark Blue Theme */}
      <div className="flex flex-wrap gap-4 items-center p-4 rounded-2xl bg-gray-900 border border-blue-900 text-blue-200">
        <div className="flex items-center gap-3">
          <button onClick={() => setZoom(z => clampZoomForContainer(z - 0.1))}
                  className="px-3 py-2 rounded-lg border border-blue-700 hover:bg-blue-700 hover:text-white transition">
            <FontAwesomeIcon icon={faMinus} />
          </button>
          <span className="w-16 text-center font-medium">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => clampZoomForContainer(z + 0.1))}
                  className="px-3 py-2 rounded-lg border border-blue-700 hover:bg-blue-700 hover:text-white transition">
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>

        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={showVendors} onChange={e => setShowVendors(e.target.checked)} className="accent-cyan-500" />
            <span>Vendors</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={showHeatmap} onChange={e => setShowHeatmap(e.target.checked)} className="accent-cyan-500" />
            <span>Rating Heatmap</span>
          </label>
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-4">
          <div className="w-32 h-3 rounded-full" style={{ background: 'linear-gradient(90deg, #111827, #1e40af, #06b6d4)' }} />
          <span className="text-sm">Rating Heat</span>
          <input
            type="text"
            placeholder="Search stalls/dishes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="px-4 py-2 rounded-xl bg-gray-800 border border-blue-900 text-blue-100 placeholder-blue-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>
      </div>

      {/* Grid */}
      <div className="p-4 rounded-2xl bg-gray-900 border border-blue-900">
        <div className="grid gap-3"
             style={{ gridTemplateColumns: `repeat(${cols}, minmax(${cellMin}px, 1fr))` }}>
          {mapGrid.map((row, r) =>
            row.map((cell, c) => {
              const items = cell.items || [];
              const visible = cellMatchesSearch(cell);
              const ratings = items.map(s => Number(s.rating || 0)).filter(r => r > 0);
              const avgRating = ratings.length ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : null;
              const heatColor = avgRating && showHeatmap ? ratingToColor(avgRating) : '#111827';
              const ownedByPlayer = items.some(s => s.vendor && ownerId && s.vendor === ownerId);

              return (
                <div
                  key={`${r}-${c}`}
                  className={`stall-spot rounded-2xl border ${ownedByPlayer ? 'border-cyan-400 shadow-cyan-500/50 shadow-lg' : 'border-blue-900'} flex flex-col items-center justify-center cursor-pointer transition-all hover:scale-105`}
                  style={{
                    opacity: visible ? 1 : 0.3,
                    backgroundColor: heatColor,
                  }}
                  onClick={() => openCell(r, c)}
                  title={`${items.length} stall(s)` + (avgRating ? ` • avg ${avgRating}⭐` : '')}
                >
                  {items.length > 0 && (
                    <div className="absolute top-2 right-2 bg-black/70 text-cyan-400 px-2 py-1 rounded-full text-xs font-bold">
                      {items.length}
                    </div>
                  )}
                  <div className="p-3 text-center">
                    {items.length > 0 ? (
                      <>
                        <img className="w-16 h-16 rounded-xl shadow-md object-cover mb-2" src={items[0].img || 'https://via.placeholder.com/80?text=Stall'} alt={items[0].name} />
                        <div className="text-blue-200 font-bold text-sm truncate max-w-full">{items[0].name}</div>
                        <div className="text-cyan-400 text-xs">{items[0].dish || items[0].location}</div>
                        {avgRating && (
                          <div className="mt-2 flex items-center justify-center gap-1">
                            {renderStars(avgRating)}
                            <span className="text-xs text-blue-300 ml-1">{avgRating}</span>
                          </div>
                        )}
                        {items[0].offer && (
                          <div className="mt-1 bg-red-600/80 text-white text-xs px-2 py-1 rounded-full animate-pulse">
                            <FontAwesomeIcon icon={faTag} /> Offer!
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="text-4xl mb-2">🍲</div>
                        <div className="text-blue-400 text-sm">Available Spot</div>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Details Drawer - Mobile Responsive */}
      {selectedCell && (() => {
        const isNarrow = window.innerWidth < 768;
        const drawerStyle = isNarrow
          ? 'fixed inset-x-4 bottom-4 top-32 z-50 bg-gray-900/95 backdrop-blur shadow-2xl rounded-2xl p-5 overflow-auto'
          : 'fixed right-4 top-24 bottom-8 w-96 z-50 bg-gray-900/95 backdrop-blur shadow-2xl rounded-2xl p-6 overflow-auto';

        return (
          <div className={drawerStyle}>
            <div className="flex justify-between items-center text-blue-200 mb-4">
              <h3 className="font-bold text-xl">Spot {selectedCell.r + 1}-{selectedCell.c + 1}</h3>
              <div className="flex gap-3">
                <button onClick={() => { navigator.clipboard?.writeText(`Spot ${selectedCell.r+1}-${selectedCell.c+1}`); alert('Copied!'); }}
                        className="p-2 rounded-lg border border-blue-700 hover:bg-blue-700 transition">
                  <FontAwesomeIcon icon={faCopy} />
                </button>
                <button onClick={closeDetails} className="p-2 rounded-lg border border-red-700 hover:bg-red-700 transition">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {selectedCell.items.length === 0 ? (
                <div className="text-center text-blue-400 py-8">
                  <div className="text-6xl mb-4">🍲</div>
                  <p className="text-lg">Prime spot available for new stall!</p>
                </div>
              ) : (
                selectedCell.items.map(stall => (
                  <div key={stall.id || stall.name} className="flex gap-4 p-4 bg-gray-800/50 rounded-xl border border-blue-900">
                    <img src={stall.img || 'https://via.placeholder.com/120?text=Stall'} alt={stall.name} className="w-24 h-24 object-cover rounded-xl shadow-md" />
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-bold text-lg text-blue-300">{stall.name}</h4>
                          <p className="text-cyan-400 text-sm">{stall.dish || stall.location}</p>
                        </div>
                        {stall.verified && (
                          <div className="bg-blue-600 text-white px-3 py-1 rounded-full flex items-center gap-1 text-sm">
                            <FontAwesomeIcon icon={faCheckCircle} /> Verified
                          </div>
                        )}
                      </div>

                      <div className="mt-3 flex items-center gap-2">
                        {renderStars(stall.rating)}
                        <span className="text-blue-200 text-sm">({stall.reviews} reviews)</span>
                      </div>

                      <p className="mt-2 text-lg font-bold text-blue-100">{stall.priceRange}</p>

                      {stall.offer && (
                        <div className="mt-2 inline-block bg-red-600 text-white px-3 py-1 rounded-full text-sm animate-pulse">
                          <FontAwesomeIcon icon={faTag} /> Special Offer!
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      <button onClick={() => onContact(stall)} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition font-medium">
                        Contact
                      </button>
                      <button onClick={() => onScheduleVisit(stall)} className="px-4 py-2 rounded-lg bg-gray-700 text-cyan-400 border border-cyan-600 hover:bg-cyan-600 hover:text-white transition font-medium">
                        Visit
                      </button>
                      <Link
                        to="/horecahub-digitaltwin"
                        state={{ stall: stall.name, dish: stall.dish, stallData: stall }}
                        className="block px-4 py-2 rounded-lg bg-cyan-600 text-black hover:bg-cyan-500 transition font-bold text-center"
                      >
                        Digital Profile
                      </Link>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
